import { useRef, useState } from 'react';
import { Camera, FileText, Loader2, Play, Trash2, Video } from 'lucide-react';
import { mediaUrl } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Dialog } from '../ui';

export type Thumb = { id: string; kind: 'photo' | 'video' | 'file'; content_type: string; localUrl?: string; pending?: boolean };

export function MediaThumbs({ items, onDelete, size = 'md' }: { items: Thumb[]; onDelete?: (id: string) => void; size?: 'sm' | 'md' }) {
  const [open, setOpen] = useState<Thumb | null>(null);
  if (items.length === 0) return null;
  const box = size === 'sm' ? 'size-14' : 'size-20';
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {items.map((m) => {
          const src = m.localUrl ?? mediaUrl(m.id);
          return (
            <div key={m.id} className={cn('group relative overflow-hidden rounded-md border bg-muted', box)}>
              <button type="button" className="size-full" onClick={() => setOpen(m)} aria-label="Open">
                {m.kind === 'photo' ? <img src={src} alt="" className="size-full object-cover" loading="lazy" />
                  : m.kind === 'video' ? <div className="flex size-full items-center justify-center"><Play className="size-6 text-muted-fg" /></div>
                    : <div className="flex size-full items-center justify-center"><FileText className="size-6 text-muted-fg" /></div>}
              </button>
              {m.pending && <div className="absolute inset-0 flex items-center justify-center bg-white/60" title="Waiting to upload"><Loader2 className="size-5 animate-spin" /></div>}
              {onDelete && !m.pending && (
                <button type="button" onClick={() => onDelete(m.id)} className="absolute top-0.5 right-0.5 rounded bg-black/60 p-1 text-white opacity-90" aria-label="Remove">
                  <Trash2 className="size-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)} title="Attachment" wide>
        {open && (open.kind === 'photo'
          ? <img src={open.localUrl ?? mediaUrl(open.id)} alt="" className="mx-auto max-h-[70dvh] rounded" />
          : open.kind === 'video'
            ? <video src={open.localUrl ?? mediaUrl(open.id)} controls className="mx-auto max-h-[70dvh] w-full rounded" />
            : <a href={mediaUrl(open.id)} target="_blank" rel="noreferrer" className="text-info underline">Open file</a>)}
      </Dialog>
    </>
  );
}

// Big touch targets that open the tablet camera directly (capture) or the photo library.
export function CaptureButtons({ onFiles, photo = true, video = false, disabled, label = 'Photo' }: {
  onFiles: (files: File[]) => void; photo?: boolean; video?: boolean; disabled?: boolean; label?: string;
}) {
  const photoRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const pick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length) onFiles(files);
  };
  const btn = 'inline-flex h-10 items-center gap-2 rounded-lg border bg-card px-3 text-sm font-medium hover:bg-muted disabled:opacity-50';
  return (
    <div className="flex flex-wrap gap-2">
      {photo && (
        <>
          <button type="button" className={btn} disabled={disabled} onClick={() => photoRef.current?.click()}><Camera className="size-4" />{label}</button>
          <input ref={photoRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={pick} />
        </>
      )}
      {video && (
        <>
          <button type="button" className={btn} disabled={disabled} onClick={() => videoRef.current?.click()}><Video className="size-4" />Video</button>
          <input ref={videoRef} type="file" accept="video/*" capture="environment" className="hidden" onChange={pick} />
        </>
      )}
    </div>
  );
}
